/**
 * The artifact gallery — filter bar, grouped grid/list, and pagination — as a
 * standalone component that can render either as the full-page view or as the
 * content pane of a surface slot.
 *
 * When syncUrlParams is true (the standalone page), scope, sort, page, and
 * the workflow filter are read from and written back to the URL. When false
 * (the embedded surface), state stays local unless a controlled value and
 * callback are supplied per field, so a host can choose which parts of the
 * embedded gallery's state belong in its own URL instead.
 */

import {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  Fragment,
  type ReactNode,
} from 'react';
import { useSearchParams } from 'react-router';
import { Grid2x2, List, Search, ChevronDown, History } from 'lucide-react';

import { useListArtifacts } from '@/hooks/queries/artifacts/use-list-artifacts';
import {
  ArtifactCard,
  ArtifactCardSkeleton,
} from '@/components/artifacts/artifact-card';
import { ArtifactWorkflowFilter } from '@/components/artifacts/artifact-workflow-filter';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import { workflowRunPath } from '@/constants/workflow-editor';
import { groupArtifactsByRun } from '@/components/artifacts/group-artifacts';
import type { ArtifactRunGroup } from '@/components/artifacts/group-artifacts';
import { Pagination } from '@/components/pagination';
import {
  ScopeTabs,
  RESOURCE_SCOPES,
  DEFAULT_RESOURCE_SCOPE,
  type ResourceScope,
} from '@/components/scope-tabs';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ShareModal } from '@/components/share-modal';
import { useOrgStore } from '@/stores/org-store';
import { cn } from '@/lib/utils';
import { ARTIFACT_SORT_KEYS, DEFAULT_ARTIFACT_SORT } from '@/types/artifacts';
import type {
  ArtifactListItem,
  ArtifactSortKey,
  ListArtifactsParams,
} from '@/types/artifacts';

type ViewMode = 'grid' | 'list';

const PAGE_SIZE = 60;

const SORT_LABELS: Record<ArtifactSortKey, string> = {
  updated_at_desc: 'Recent',
  updated_at_asc: 'Oldest first',
  title_asc: 'Name A→Z',
  title_desc: 'Name Z→A',
};

export function ArtifactGroupsSkeleton({ viewMode }: { viewMode: ViewMode }) {
  return (
    <div className='flex flex-col gap-6'>
      {Array.from({ length: 2 }).map((_, section) => (
        <Fragment key={section}>
          <Separator />
          <div className='flex flex-col gap-4'>
            <Skeleton className='h-5 w-72' />
            {viewMode === 'grid' ? (
              <div className='grid grid-cols-1 items-start gap-4 sm:grid-cols-2 lg:grid-cols-3'>
                {Array.from({ length: 3 }).map((_, i) => (
                  <ArtifactCardSkeleton key={i} viewMode='grid' />
                ))}
              </div>
            ) : (
              <div className='flex flex-col gap-2'>
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className='h-14 w-full rounded-lg' />
                ))}
              </div>
            )}
          </div>
        </Fragment>
      ))}
    </div>
  );
}

export function ArtifactGroupSections({
  groups,
  viewMode,
  onShare,
  onWorkflowClick,
  onArtifactOpen,
}: {
  groups: ArtifactRunGroup[];
  viewMode: ViewMode;
  onShare: (artifact: ArtifactListItem) => void;
  onWorkflowClick: (workflowId: string, runId: string | null) => void;
  onArtifactOpen?: (artifact: ArtifactListItem) => void;
}) {
  return (
    <div className='flex flex-col gap-6'>
      {groups.map((group) => (
        <Fragment key={group.key}>
          <Separator />
          <section className='flex flex-col gap-4'>
            <div className='flex items-center gap-2 pb-2 text-sm font-medium'>
              {group.workflowId ? (
                <Button
                  type='button'
                  variant='link'
                  className='h-auto p-0 text-sm font-medium text-foreground'
                  onClick={() =>
                    onWorkflowClick(group.workflowId!, group.runId)
                  }
                >
                  {group.workflowName}
                </Button>
              ) : (
                <span className='text-foreground'>{group.workflowName}</span>
              )}
              {group.runDateLabel && (
                <span className='text-muted-foreground'>
                  {group.runDateLabel}
                </span>
              )}
            </div>
            {viewMode === 'grid' ? (
              <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'>
                {group.artifacts.map((artifact) => (
                  <ArtifactCard
                    key={artifact.id}
                    artifact={artifact}
                    viewMode='grid'
                    onShare={onShare}
                    onOpen={onArtifactOpen}
                  />
                ))}
              </div>
            ) : (
              <div className='flex flex-col gap-2'>
                {group.artifacts.map((artifact) => (
                  <ArtifactCard
                    key={artifact.id}
                    artifact={artifact}
                    viewMode='list'
                    onShare={onShare}
                    onOpen={onArtifactOpen}
                  />
                ))}
              </div>
            )}
          </section>
        </Fragment>
      ))}
    </div>
  );
}

export interface ArtifactsGalleryProps {
  /**
   * Pre-filter artifacts by this workflow. Sets the initial workflow filter
   * value; the user can clear it via the filter combobox. Ignored when
   * syncUrlParams is true and a ?workflow= URL param is also present.
   */
  workflowId?: string;
  /**
   * When true, scope, sort, page, and the workflow filter are read from and
   * written to the URL search params. Use for the standalone /artifacts page
   * so deep links and browser history work.
   *
   * When false (default), state stays local unless a controlled value and
   * callback are supplied. Use when the gallery is embedded in a surface slot
   * so its host can choose which state belongs in the workspace URL.
   */
  syncUrlParams?: boolean;
  /**
   * Which My/Shared tab to show, for a host that keeps the choice in its own
   * URL. Pass with onScopeChange: together they hand the tab to the host,
   * which is the only way an embedded gallery's choice survives a reload.
   */
  scope?: ResourceScope;
  /**
   * Called when the user switches My/Shared. Supplying it makes the tab
   * controlled -- the gallery stops keeping the choice itself.
   */
  onScopeChange?: (scope: ResourceScope) => void;
  /** Sort selected by a host that persists the embedded gallery's filters. */
  sort?: ArtifactSortKey;
  /** Called when the user selects a different sort order. */
  onSortChange?: (sort: ArtifactSortKey) => void;
  /**
   * Called when the workflow filter changes. Supplying it makes workflowId
   * controlled so the host can persist the selection.
   */
  onWorkflowFilterChange?: (workflowId: string | null) => void;
  /**
   * Hide the workflow filter and keep the gallery on this workflow. Used when
   * the pane was opened from a specific workflow rather than as a browser.
   */
  lockWorkflowFilter?: boolean;
  /**
   * Called when the user navigates to a workflow from a group header. Defaults
   * to navigating to the workflow editor. Override when a host can show the
   * workflow in a pane instead.
   */
  onWorkflowNavigate?: (workflowId: string, runId: string | null) => void;
  /**
   * Called when an artifact card is clicked. When supplied the artifact opens
   * in the host's surface pane instead of navigating to the standalone detail
   * route. The callback receives the full list item so the caller can carry
   * `user_role`, which the detail endpoint does not return.
   *
   * When omitted (the standalone /artifacts page), ArtifactCard falls back to
   * navigating to the detail route, which is the current behaviour.
   */
  onArtifactOpen?: (artifact: ArtifactListItem) => void;
  /**
   * Rendered at the end of the filter row, after the view toggle. Lets a host
   * put its own chrome — a close button, say — on the same line as the
   * gallery's controls rather than floating it over the content.
   */
  trailingAction?: ReactNode;
}

export function ArtifactsGallery({
  workflowId: initialWorkflowId,
  syncUrlParams = false,
  scope: controlledScope,
  onScopeChange,
  sort: controlledSort,
  onSortChange,
  onWorkflowFilterChange,
  lockWorkflowFilter = false,
  onWorkflowNavigate,
  onArtifactOpen,
  trailingAction,
}: ArtifactsGalleryProps) {
  const { currentOrg } = useOrgStore();
  const navigate = useOrgNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [localScope, setLocalScope] = useState<ResourceScope>(
    DEFAULT_RESOURCE_SCOPE,
  );
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    return (localStorage.getItem('artifacts-view-mode') as ViewMode) ?? 'grid';
  });
  const [shareTarget, setShareTarget] = useState<ArtifactListItem | null>(null);
  const [searchVisible, setSearchVisible] = useState(false);
  const prevDebouncedSearchRef = useRef(debouncedSearch);
  const searchDebounceTimerRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  // When syncing URL params, read state from URL; otherwise use local state.
  const pageFromUrl = parseInt(searchParams.get('page') || '1', 10);
  const workflowIdFromUrl = searchParams.get('workflow') || null;
  const sortFromUrl = searchParams.get('sort');
  const scopeFromUrl = searchParams.get('scope');

  const [localPage, setLocalPage] = useState(1);
  const [localSort, setLocalSort] = useState<ArtifactSortKey>(
    DEFAULT_ARTIFACT_SORT,
  );
  const [localWorkflowId, setLocalWorkflowId] = useState<string | null>(
    initialWorkflowId ?? null,
  );

  const currentPage = syncUrlParams
    ? pageFromUrl > 0
      ? pageFromUrl
      : 1
    : localPage;
  const sort = onSortChange
    ? (controlledSort ?? DEFAULT_ARTIFACT_SORT)
    : syncUrlParams
      ? (ARTIFACT_SORT_KEYS.find((key) => key === sortFromUrl) ??
        DEFAULT_ARTIFACT_SORT)
      : localSort;
  const currentScope = onScopeChange
    ? (controlledScope ?? DEFAULT_RESOURCE_SCOPE)
    : syncUrlParams
      ? (RESOURCE_SCOPES.find((key) => key === scopeFromUrl) ??
        DEFAULT_RESOURCE_SCOPE)
      : localScope;
  const selectedWorkflowId = onWorkflowFilterChange
    ? (initialWorkflowId ?? null)
    : localWorkflowId;

  // When syncing, keep selected workflow in sync with the URL param.
  useEffect(() => {
    if (syncUrlParams) {
      setLocalWorkflowId(workflowIdFromUrl);
    }
  }, [syncUrlParams, workflowIdFromUrl]);

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem('artifacts-view-mode', mode);
  };

  const resetPage = useCallback(() => {
    if (syncUrlParams) {
      const newParams = new URLSearchParams(searchParams);
      if (newParams.get('page') !== '1') {
        newParams.set('page', '1');
        setSearchParams(newParams, { replace: true });
      }
    } else {
      setLocalPage(1);
    }
  }, [syncUrlParams, searchParams, setSearchParams]);

  const handlePageChange = useCallback(
    (page: number) => {
      if (syncUrlParams) {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('page', String(page));
        setSearchParams(newParams);
      } else {
        setLocalPage(page);
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [syncUrlParams, searchParams, setSearchParams],
  );

  const handleScopeChange = useCallback(
    (newScope: ResourceScope) => {
      if (onScopeChange) {
        onScopeChange(newScope);
        resetPage();
      } else if (syncUrlParams) {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('scope', newScope);
        newParams.set('page', '1');
        setSearchParams(newParams, { replace: true });
      } else {
        setLocalScope(newScope);
        resetPage();
      }
    },
    [onScopeChange, syncUrlParams, searchParams, setSearchParams, resetPage],
  );

  const handleSortChange = useCallback(
    (key: ArtifactSortKey) => {
      if (onSortChange) {
        onSortChange(key);
        resetPage();
      } else if (syncUrlParams) {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('sort', key);
        newParams.set('page', '1');
        setSearchParams(newParams, { replace: true });
      } else {
        setLocalSort(key);
        setLocalPage(1);
      }
    },
    [onSortChange, resetPage, syncUrlParams, searchParams, setSearchParams],
  );

  const handleWorkflowFilterChange = useCallback(
    (workflowId: string | null) => {
      if (onWorkflowFilterChange) {
        onWorkflowFilterChange(workflowId);
        resetPage();
      } else {
        setLocalWorkflowId(workflowId);
      }
      if (!onWorkflowFilterChange && syncUrlParams) {
        const newParams = new URLSearchParams(searchParams);
        if (workflowId) {
          newParams.set('workflow', workflowId);
        } else {
          newParams.delete('workflow');
        }
        newParams.set('page', '1');
        setSearchParams(newParams, { replace: true });
      } else if (!onWorkflowFilterChange) {
        setLocalPage(1);
      }
    },
    [
      onWorkflowFilterChange,
      resetPage,
      syncUrlParams,
      searchParams,
      setSearchParams,
    ],
  );

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }
    searchDebounceTimerRef.current = setTimeout(
      () => setDebouncedSearch(value),
      300,
    );
  }, []);

  // Reset to page 1 when search changes.
  useEffect(() => {
    if (
      prevDebouncedSearchRef.current !== debouncedSearch &&
      currentPage !== 1
    ) {
      resetPage();
    }
    prevDebouncedSearchRef.current = debouncedSearch;
  }, [debouncedSearch, currentPage, resetPage]);

  useEffect(() => {
    if (searchVisible) {
      searchContainerRef.current?.querySelector('input')?.focus();
    }
  }, [searchVisible]);

  const handleSearchClose = useCallback(() => {
    setSearchVisible(false);
    setSearch('');
    setDebouncedSearch('');
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }
  }, []);

  const params: ListArtifactsParams = {
    scope: currentScope,
    search: debouncedSearch || undefined,
    workflow_id: selectedWorkflowId ?? undefined,
    sort,
    limit: PAGE_SIZE,
    offset: (currentPage - 1) * PAGE_SIZE,
  };

  const { data, isLoading, error } = useListArtifacts(params);

  const getEmptyStateMessage = () => {
    if (debouncedSearch) return 'No artifacts match your search.';
    if (selectedWorkflowId) return 'No artifacts from this workflow yet.';
    if (currentScope === 'shared')
      return 'No artifacts have been shared with you yet.';
    return 'No artifacts yet. Artifacts are created when your workflows produce outputs.';
  };

  const artifacts = data?.artifacts;
  const artifactGroups = useMemo(
    () => groupArtifactsByRun(artifacts ?? [], sort),
    [artifacts, sort],
  );
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, total);

  // Clamp page when total shrinks (e.g. after delete or filter change).
  useEffect(() => {
    if (!isLoading && total > 0 && currentPage > totalPages) {
      handlePageChange(totalPages);
    }
  }, [currentPage, totalPages, total, isLoading, handlePageChange]);

  const handleWorkflowClick = (workflowId: string, runId: string | null) => {
    if (onWorkflowNavigate) {
      onWorkflowNavigate(workflowId, runId);
    } else {
      navigate(workflowRunPath(workflowId, runId ?? undefined));
    }
  };

  return (
    <div className='flex flex-col gap-6'>
      {/* Filter bar */}
      <div className='flex items-center justify-between gap-4'>
        {/* Left: scope tabs + search icon */}
        <div className='flex items-center gap-0'>
          <ScopeTabs
            value={currentScope}
            onValueChange={handleScopeChange}
            mineLabel='My Artifacts'
          />

          {/* Expandable search — grows in place of the icon */}
          <div
            ref={searchContainerRef}
            className={cn(
              'ml-1 h-9 overflow-hidden transition-[width] duration-200 ease-out',
              searchVisible
                ? 'w-56 animate-in fade-in-0 slide-in-from-left-2'
                : 'w-9',
            )}
            onKeyDown={(e) => {
              if (e.key === 'Escape' && searchVisible) {
                handleSearchClose();
              }
            }}
          >
            {searchVisible ? (
              <SearchInput
                value={search}
                onChange={handleSearchChange}
                placeholder='Search by file or workflow name...'
                aria-label='Search artifacts by file or workflow name'
              />
            ) : (
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='size-9 hover:text-foreground'
                onClick={() => setSearchVisible(true)}
                aria-label='Open search'
                aria-expanded={false}
              >
                <Search className='size-4' />
              </Button>
            )}
          </div>
        </div>

        {/* Right: workflow filter + sort + view toggle */}
        <div className='flex items-center gap-2'>
          {!lockWorkflowFilter && (
            <ArtifactWorkflowFilter
              value={selectedWorkflowId}
              onChange={handleWorkflowFilterChange}
            />
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant='ghost' className='gap-1 text-sm font-medium'>
                <History className='size-4 shrink-0' /> {SORT_LABELS[sort]}
                <ChevronDown className='size-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              {ARTIFACT_SORT_KEYS.map((key) => (
                <DropdownMenuItem
                  key={key}
                  onClick={() => handleSortChange(key)}
                >
                  {SORT_LABELS[key]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <div className='flex items-center gap-1'>
            <Button
              variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
              size='icon'
              className='h-9 w-9'
              onClick={() => handleViewModeChange('grid')}
            >
              <Grid2x2 className='h-4 w-4' />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size='icon'
              className='h-9 w-9'
              onClick={() => handleViewModeChange('list')}
            >
              <List className='h-4 w-4' />
            </Button>
          </div>

          {trailingAction && (
            <>
              <Separator orientation='vertical' className='h-5' />
              {trailingAction}
            </>
          )}
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <ArtifactGroupsSkeleton viewMode={viewMode} />
      ) : error ? (
        <div className='py-12 text-center text-muted-foreground'>
          Failed to load artifacts. Please try again.
        </div>
      ) : !artifacts?.length ? (
        <div className='py-12 text-center text-muted-foreground'>
          {getEmptyStateMessage()}
        </div>
      ) : (
        <ArtifactGroupSections
          groups={artifactGroups}
          viewMode={viewMode}
          onShare={setShareTarget}
          onWorkflowClick={handleWorkflowClick}
          onArtifactOpen={onArtifactOpen}
        />
      )}

      {/* Pagination footer */}
      {!isLoading && total > 0 && (
        <div className='flex flex-col items-center gap-4 sm:flex-row sm:flex-wrap sm:justify-between'>
          <p className='text-sm text-muted-foreground'>
            Showing {rangeStart}–{rangeEnd} of {total} artifacts
          </p>
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      )}

      {/* Share modal — the gallery owns its own share actions */}
      {shareTarget && (
        <ShareModal
          resourceType='artifact'
          resourceId={shareTarget.id}
          orgId={currentOrg?.orgid ?? null}
          open={!!shareTarget}
          onOpenChange={(open) => {
            if (!open) setShareTarget(null);
          }}
        />
      )}
    </div>
  );
}
